import React, { useEffect, useState } from "react";
import { CheckToken } from "../../login/AccessToken";
import axios from "../../login/Axios-interceptor";
import VisibilityIcon from "@mui/icons-material/Visibility";

import {
  DataGrid,
  GridToolbarColumnsButton,
  GridToolbarFilterButton,
  GridToolbarDensitySelector, 
} from "@mui/x-data-grid";
import { toast } from "react-toastify";
import Fab from "@mui/material/Fab";
import Tooltip from "@mui/material/Tooltip";
import Drawer from "@mui/material/Drawer";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import RefreshIcon from "@mui/icons-material/Refresh";
import "./UserComponents.scss";
import AddLinkIcon from "@mui/icons-material/AddLink";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import { usePermissions } from "../../login/PermissionContext";
import { postHipLinkCareContext } from "../../doctor-console/Utils/hipLinkCareContextApi";
import {
  buildHipLinkCareContextPatientEntries,
  buildHipLinkCareContextPostBody,
  formatAbhaNumberForHipLink,
  resolveHipLinkHiTypeFromDocumentType,
} from "../../doctor-console/Utils/UtilsFunctions";

function UploadReprortTable({ refresh, setAiResponse, setSelectedPatient }) {
  const [userData, setUserData] = useState([]);
  const [authToken, setAuthToken] = useState(
    sessionStorage.getItem("accessToken") ?? ""
  );
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [isRefreshingStatus, setIsRefreshingStatus] = useState(false);
  const headers = React.useMemo(
    () => ({ Authorization: `Bearer ${authToken}` }),
    [authToken]
  );
  const REACT_APP_BACKEND_ROUTE_URL = process.env.REACT_APP_BACKEND_ROUTE_URL;
  const { userInfo } = usePermissions();
  const isAIEnabled = userInfo.ai_enabled;

  const Token = React.useCallback(() => {
    const check = CheckToken();
    setAuthToken(sessionStorage.getItem("accessToken"));
    return check;
  }, []);

  const getUploadData = React.useCallback(() => {
    return axios
      .post(
        `${REACT_APP_BACKEND_ROUTE_URL}get_data_upload_status/`,
        {},
        {
          headers: headers,
        }
      )
      .then((res) => {
        setUserData(res.data?.data);
      })
      .catch((err) => {
        console.log(err);
        toast.error(err);
      });
  }, [REACT_APP_BACKEND_ROUTE_URL, headers]);

  useEffect(() => {
    // Fetch upload status only when the drawer is opened (fab-button click),
    // and keep refresh behavior the same while the drawer remains open.
    if (!drawerOpen) return;
    if (Token()) {
      getUploadData();
    }
  }, [drawerOpen, Token, getUploadData, refresh]);

  // Manual re-fetch of get_data_upload_status/. hip_link_carecontext/ linking is
  // settled asynchronously on ABDM's side, so the row can still read "Not Linked
  // Yet" right after linking — this lets the user pull the latest status.
  const handleRefreshStatus = () => {
    if (isRefreshingStatus) return;
    if (!Token()) return;
    setIsRefreshingStatus(true);
    Promise.resolve(getUploadData()).finally(() => {
      setIsRefreshingStatus(false);
    });
  };

  const handleDrawerOpen = () => {
    setDrawerOpen(true);
  };

  const handleDrawerClose = () => {
    setDrawerOpen(false);
  };

  const getRowClassName = (params) => {
    return "custom-row";
  };
  // point is  get_data_upload_status/ rows carry the ABHA address but not the 14-digit
  // ABHA number, which hip_link_carecontext/ requires. Look the patient up by
  // ID (search matches patient name / ID / phone) to get the full ABHA info.
  const fetchPatientAbhaInfo = React.useCallback(
    (patientId) =>
      axios
        .get(`${REACT_APP_BACKEND_ROUTE_URL}get_patient_list_with_aabha_info/`, {
          headers: headers,
          params: { search: patientId },
        })
        .then(
          (res) =>
            (res.data?.data || []).find((p) => p.patient_id === patientId) ||
            null
        )
        .catch((err) => {
          console.log(err);
          return null;
        }),
    [REACT_APP_BACKEND_ROUTE_URL, headers]
  );

  const linkToAbdm = (row) => {
    console.log("Link to ABDM for:", row);

    fetchPatientAbhaInfo(row.patient_id).then((patientInfo) => {
      const abhaNumber = formatAbhaNumberForHipLink(
        patientInfo?.abha_no ?? row.abha_no
      );
      if (!abhaNumber) {
        toast.error(
          "ABHA number not available for this patient. Cannot link to ABDM."
        );
        return;
      }

      const formData = new FormData();
      formData.append("patient_id", row.patient_id);
      formData.append("careContextReferenceID", row.care_context);

      axios
        .post(
          `${REACT_APP_BACKEND_ROUTE_URL}save_pending_care_context_linking/`,
          formData,
          {
            headers: {
              ...headers,
              "Content-Type": "multipart/form-data",
            },
          }
        )
        .then((response) => {
          console.log("Response data:", response.data);

          // M2 linking: single hip_link_carecontext/ call replaces the
          // auth_init -> auth_confirm -> add_context workflow chain.
          const patientData = {
            abha_no: patientInfo?.abha_no ?? row.abha_no,
            abha_id: row.abha_id ?? patientInfo?.abha_id,
            name: patientInfo?.name ?? row.patient_name,
            first_name: patientInfo?.first_name,
            middle_name: patientInfo?.middle_name,
            last_name: patientInfo?.last_name,
            gender: patientInfo?.gender ?? row.gender,
            year_of_birth: row.year_of_birth,
            dob: patientInfo?.dob,
          };

          const postBody = buildHipLinkCareContextPostBody({
            abhaNumber,
            abhaAddress: row.abha_id ?? patientInfo?.abha_id,
            phoneNo: patientInfo?.phone_no ?? row.phone_no,
            patientEntries: buildHipLinkCareContextPatientEntries({
              referenceNumber: row.patient_id,
              display: row.abha_id,
              careContextReference: row.care_context,
              careContextDisplay: row.display,
              hiTypes: resolveHipLinkHiTypeFromDocumentType(row.hi_type),
            }),
            // Sent as null for now; the pending row is tracked by
            // save_pending_care_context_linking/ above.
            careContextTransactionId: null,
          });

          return postHipLinkCareContext({
            backendUrl: REACT_APP_BACKEND_ROUTE_URL,
            postBody,
            headers,
            patientData,
          });
        })
        .then((res) => {
          console.log("Hip Link Care Context Result", res.data);
          const status = String(res.data?.status ?? "").toLowerCase();
          if (status === "success") {
            toast.success("Care Context Linked");
          } else {
            toast.error(res.data?.message || "some error occurred");
          }
          getUploadData();
        })
        .catch((err) => {
          console.log(err);
          toast.error(err);
        });
    });
  };
  const handleViewReport = (patient) => {
    console.log("View Report for:", patient?.job_id);

    axios
      .get(
        `${REACT_APP_BACKEND_ROUTE_URL}get_diagnostic_report/${patient?.job_id}/`,
        {
          headers: headers,
        }
      )
      .then((response) => {
        console.log("response for get diagnostic report", response);
        if (response.data.status === "Success") {
          setAiResponse(response?.data?.data);
          setSelectedPatient(patient);
        } else {
          toast.error(response?.data?.message || "Error fetching diagnostic report");
        }
      })
      .catch((err) => {
        console.log(err);
        toast.error(
          err?.response?.data?.message || err.message || "AI upload failed."
        );
      });
  };
  const sortedUserData = [...userData].sort(
    (a, b) => new Date(b.upload_time) - new Date(a.upload_time)
  );

  return (
    <div className="upload-report-table">
      <Tooltip title="See Upload Status">
        <Fab className="fab-button" color="primary" onClick={handleDrawerOpen}>
          <ArrowForwardIcon />
        </Fab>
      </Tooltip>

      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={handleDrawerClose}
        PaperProps={{
          sx: { width: "100%", height: "100%" },
        }}
      >
        <div className="drawer-content">
          {/* <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              padding: "16px",
            }}
          >
            <Button onClick={handleDrawerClose}>
              <ArrowBackIcon />
            </Button>
            <div className="main-title" style={{ textAlign: "center" }}>
              Upload Status
            </div>
           
          </div> */}
          <div
            style={{
              position: "relative",
              display: "flex",
              alignItems: "center",
              padding: "16px",
            }}
          >
            {/* Left Arrow */}
            <Button
              onClick={handleDrawerClose}
              style={{ position: "absolute", left: "16px" }}
            >
              <ArrowBackIcon style={{ marginBottom: "20px" }} />
            </Button>

            {/* Center Title */}
            <div
              className="main-title"
              style={{
                textAlign: "center",
                flex: 1,
              }}
            >
              Upload Status
            </div>

            {/* Right: manual get_data_upload_status/ refresh */}
            <Tooltip title="Fetch the latest upload / ABDM linking status">
              <span style={{ position: "absolute", right: "16px" }}>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={handleRefreshStatus}
                  disabled={isRefreshingStatus}
                  startIcon={
                    isRefreshingStatus ? (
                      <CircularProgress size={16} color="inherit" />
                    ) : (
                      <RefreshIcon />
                    )
                  }
                  style={{ marginBottom: "20px", textTransform: "none" }}
                >
                  {isRefreshingStatus ? "Refreshing..." : "Refresh Status"}
                </Button>
              </span>
            </Tooltip>
          </div>

          <div className="data-grid-container">
            {userData.length > 0 ? (
              <DataGrid
                rows={sortedUserData}
                columns={[
                  { field: "abha_id", headerName: "ABHA Address", width: 150 },
                  { field: "patient_id", headerName: "Patient ID", width: 150 },
                  {
                    field: "patient_name",
                    headerName: "Patient Name",
                    width: 150,
                  },
                  {
                    field: "care_context",
                    headerName: "Care Context",
                    width: 150,
                  },
                  { field: "hi_type", headerName: "HI Type", width: 150 },
                  {
                    field: "structure_type",
                    headerName: "Structure Type",
                    width: 150,
                  },
                  //  Removed "status" column completely

                  {
                    field: "upload_time",
                    headerName: "Upload Time",
                    width: 200,
                    valueFormatter: (params) =>
                      new Date(params.value).toDateString() +
                      " " +
                      new Date(params.value).toLocaleTimeString(),
                  },
                  {
                    field: "status",
                    headerName: "Processing Status",
                    width: 200,
                    renderCell: (params) => {
                      const isDiagUnstructured = params?.row?.hi_type === "diagonstic_report" && params?.row?.structure_type === "Unstructured";
                      return (<div
                        style={{
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "center",
                          width: "100%",
                        }}
                      >
                        {params?.row?.status === "pending" ||
                          params?.row?.status === "Pending" ? (
                          !isAIEnabled ? "Uploaded" : isDiagUnstructured ? "In Process" : params?.row?.abha_id === null ? "Completed" : "Not Linked Yet"
                        ) : params?.row?.status === "processed" ||
                          params?.row?.status === "Processed" ? (
                          <Tooltip
                            title="View Report"
                            sx={{ cursor: "pointer" }}
                            onClick={() => handleViewReport(params?.row)}
                          >
                            <VisibilityIcon style={{ color: "blue" }} />
                          </Tooltip>
                        ) : params?.row?.status === "linked" ||
                          params?.row?.status === "Linked" ? (
                          "Completed"
                        ) : params?.row?.status === "generated" ||
                          params?.row?.status === "Generated" ? (
                          params.row.abha_id ? params.row.abdm_linked ? "Completed" : "Not Linked Yet" : "Completed"
                        ) : (
                          params?.row?.status
                        )}
                      </div>)
                    },
                  },
                  {
                    field: "abdm_linked",
                    headerName: "ABDM Linking Status",
                    width: 200,
                    renderCell: (params) => (
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "center",
                          width: "100%",
                        }}
                      >
                        {!params.row.abha_id ? (
                          <CloseIcon style={{ color: "red" }} />
                        ) : params.row.abdm_linked ? (
                          <CheckIcon style={{ color: "green" }} />
                        ) : (
                          <CloseIcon style={{ color: "red" }} />
                        )}
                      </div>
                    ),
                  },
                  {
                    field: "linkToAbdm",
                    headerName: "Link to ABDM",
                    width: 150,
                    renderCell: (params) => {
                      if (params.row.abha_id === "null" || params.row.abha_id === null) {
                        //  ABHA is null => disable AddLink icon
                        return (
                          <div
                            style={{
                              display: "flex",
                              justifyContent: "center",
                              alignItems: "center",
                              width: "100%",
                            }}
                          >
                            <Tooltip title="ABHA Address Missing">
                              <AddLinkIcon
                                style={{
                                  fontSize: "30px",
                                  color: "grey",
                                  opacity: 0.5,
                                  cursor: "not-allowed",
                                }}
                              />
                            </Tooltip>
                          </div>
                        );
                      } else if (
                        isAIEnabled &&
                        (params.row.status === "processed" ||
                          params.row.status === "Processed" ||
                          (params.row.status === "Pending" &&
                            params.row.hi_type === "diagonstic_report" &&
                            params.row.structure_type === "Unstructured"))
                      ) {
                        // ABHA exists but not linked => clickable AddLink
                        return (
                          <div
                            style={{
                              display: "flex",
                              justifyContent: "center",
                              alignItems: "center",
                              width: "100%",
                            }}
                          >
                            <Tooltip title="PDF Processing Remaining">
                              <AddLinkIcon
                                style={{
                                  fontSize: "30px",
                                  color: "grey",
                                  opacity: 0.5,
                                  cursor: "not-allowed",
                                }}
                              />
                            </Tooltip>
                          </div>
                        );
                      } else if (params.row.abdm_linked === false) {
                        // ABHA exists but not linked => clickable AddLink
                        return (
                          <div
                            style={{
                              display: "flex",
                              justifyContent: "center",
                              alignItems: "center",
                              width: "100%",
                            }}
                          >
                            <Tooltip title="Link">
                              <AddLinkIcon
                                style={{ cursor: "pointer", fontSize: "30px" }}
                                onClick={() => linkToAbdm(params.row)}
                              />
                            </Tooltip>
                          </div>
                        );
                      }
                      return null; // Already linked => nothing
                    },
                  },
                ]}
                // initialState={{
                //   pagination: {
                //     paginationModel: { page: 0, pageSize: 10 },
                //   },
                // }}
                initialState={{
                  pagination: {
                    paginationModel: { page: 0, pageSize: 10 },
                  },
                  sorting: {
                    sortModel: [{ field: "upload_time", sort: "desc" }],
                  },
                }}
                pageSizeOptions={[5, 10]}
                getRowId={(row) => row.patient_id + row.upload_time}
                components={{
                  Toolbar: () => (
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                      }}
                    >
                      <div style={{ display: "flex" }}>
                        <GridToolbarColumnsButton />
                        <GridToolbarFilterButton />
                        <GridToolbarDensitySelector />
                      </div>
                    </div>
                  ),
                }}
                getRowClassName={getRowClassName}
                sx={{
                  "& .MuiDataGrid-root": {
                    border: "1px solid #e0e0e0",
                    borderRadius: "4px",
                    fontFamily: "Arial, sans-serif",
                  },
                  "& .MuiDataGrid-columnHeaders": {
                    backgroundColor: "#125386",
                    color: "white",
                    fontSize: "13px",
                    fontWeight: "600",
                    minHeight: "48px !important",
                    "& .MuiDataGrid-columnHeaderTitle": {
                      color: "white",
                      fontWeight: "600",
                      fontSize: "13px",
                    },
                    "& .MuiDataGrid-columnHeader": {
                      borderRight: "1px solid rgba(255, 255, 255, 0.3)",
                      padding: "0 12px",
                    },
                    "& .MuiDataGrid-columnHeader:last-child": {
                      borderRight: "none",
                    },
                    "& .MuiDataGrid-iconButtonContainer": {
                      color: "white",
                    },
                    "& .MuiDataGrid-menuIcon": {
                      color: "white",
                    },
                    "& .MuiDataGrid-sortIcon": {
                      color: "white",
                    },
                    "& .MuiDataGrid-columnHeaderSortable": {
                      "& .MuiDataGrid-sortIcon": {
                        color: "white",
                      },
                    },
                    "& .MuiDataGrid-columnHeaderTitleContainer": {
                      "& .MuiDataGrid-sortIcon": {
                        color: "white !important",
                      },
                    },
                    "& .MuiSvgIcon-root": {
                      color: "white",
                    },
                  },
                  "& .MuiDataGrid-row": {
                    backgroundColor: "white",
                    minHeight: "40px !important",
                    "&:nth-of-type(even)": {
                      backgroundColor: "white",
                    },
                    "&:hover": {
                      backgroundColor: "#f8f9fa",
                    },
                    "&.Mui-selected": {
                      backgroundColor: "#e3f2fd",
                      "&:hover": {
                        backgroundColor: "#e3f2fd",
                      },
                    },
                    borderBottom: "1px solid #e0e0e0",
                  },
                  "& .MuiDataGrid-cell": {
                    borderRight: "1px solid #e0e0e0",
                    fontSize: "12px",
                    color: "#374151",
                    padding: "8px 12px",
                    minHeight: "40px !important",
                    "&:last-child": {
                      borderRight: "none",
                    },
                    "&:focus": {
                      outline: "none",
                    },
                  },
                  "& .MuiDataGrid-cellContent": {
                    fontSize: "12px",
                    color: "#374151",
                    fontWeight: "400",
                  },
                  "& .MuiDataGrid-footerContainer": {
                    backgroundColor: "#f8f9fa",
                    borderTop: "1px solid #e0e0e0",
                    minHeight: "52px",
                  },
                  "& .MuiTablePagination-root": {
                    color: "#374151",
                    fontSize: "12px",
                  },
                  "& .MuiDataGrid-toolbarContainer": {
                    backgroundColor: "#f8f9fa",
                    borderBottom: "1px solid #e0e0e0",
                    padding: "8px 16px",
                  },
                  "& .MuiDataGrid-columnHeaderTitle": {
                    color: "white !important", // 👈 this overrides black
                  },
                }}
              />
            ) : (
              <h3 className="no-report">No Report found!!</h3>
            )}
          </div>
        </div>
      </Drawer>
    </div>
  );
}

export default UploadReprortTable;
